import {
  Box,
  Button,
  Heading,
  HStack,
  Icon,
  Input,
  InputElement,
  InputGroup,
  Spinner,
  Tabs,
  Text,
  VStack
} from "@chakra-ui/react"
import { toaster } from "../toaster"
import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import HomeOpportunityCard from "../components/sourcing/HomeOpportunityCard"
import OpportunityCard, {
  formatAddedDate,
  getListingStatusBadge,
  getOpportunityPropertyDetailLine,
} from "../components/sourcing/OpportunityCard"
import PortfolioContent from "../components/portfolio/PortfolioContent"
import { SelectFilter } from "../components/SelectFilter"
import StateBox from "../components/common/StateBox"
import ViewModeToggle, { type ViewMode } from "../components/common/ViewModeToggle"
import PropertyMapView, {
  toMapCoordinate,
  type PropertyMapItem,
} from "../components/maps/PropertyMapView"
import { useActivePortfolio } from "../hooks/useActivePortfolio"
import { useDebounce } from "../hooks/useDebounce"
import { useInfiniteScroll } from "../hooks/useInfiniteScroll"
import { useLocalStorage } from "../hooks/useLocalStorage"
import {
  useInfiniteOpportunities,
  useMarkAsInterested
} from "../hooks/useSourcingQueries"
import type { OpportunityFilters } from "../services/sourcingApi"
import { formatCurrency } from "../utils/formatters"
import { titleizeAddress } from "../utils/propertyUtils"

const isBatchImportEnabled =
  process.env.REACT_APP_ENABLE_BATCH_IMPORT === "true"

// ─── Sourced by Greenflip tab ─────────────────────────────────────────────────

interface SourcedTabProps {
  portfolioId: number | undefined
  debouncedSearch: string
  builtForm: string
  ordering: OpportunityFilters["ordering"]
  viewMode: ViewMode
}

const SourcedTab: React.FC<SourcedTabProps> = ({
  portfolioId,
  debouncedSearch,
  builtForm,
  ordering,
  viewMode,
}) => {
  const navigate = useNavigate()
  const filters: OpportunityFilters = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      builtForm: builtForm || undefined,
      ordering,
    }),
    [debouncedSearch, builtForm, ordering]
  )

  const {
    data: opportunitiesPages,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteOpportunities(portfolioId, filters)

  const markMutation = useMarkAsInterested()

  const opportunities = useMemo(
    () =>
      opportunitiesPages?.pages
        .flatMap((page) => page.results)
        .filter((o) => o.kanbanStage !== "interested") ?? [],
    [opportunitiesPages]
  )

  const totalCount = opportunitiesPages?.pages[0]?.count ?? 0

  const mapItems = useMemo<PropertyMapItem[]>(
    () =>
      opportunities.map((opportunity) => {
        const { scrapedProperty } = opportunity
        const { home } = scrapedProperty
        const statusBadge = getListingStatusBadge(opportunity.listingStatus)
        return {
          id: `opportunity-${opportunity.id}`,
          homeId: home.id,
          opportunityId: opportunity.id,
          address: titleizeAddress(scrapedProperty.address),
          lat: toMapCoordinate(home.lat),
          lng: toMapCoordinate(home.lng),
          imageUrl: home.imageUrl,
          detailLine: getOpportunityPropertyDetailLine(scrapedProperty),
          badges: [statusBadge],
          meta: [
            scrapedProperty.price ? formatCurrency(scrapedProperty.price) : null,
            formatAddedDate(opportunity.createdAt),
          ].filter(Boolean) as string[],
          roi: home.upgradesRoi,
          upgradeCost: home.upgradesTotalCost,
          paybackPeriod: home.upgradesPaybackPeriod,
        }
      }),
    [opportunities]
  )

  const loadMoreRef = useInfiniteScroll(fetchNextPage, hasNextPage, isFetchingNextPage)

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={12}>
        <Spinner size="xl" color="green.400" />
      </Box>
    )
  }

  const hasData = opportunities.length > 0 || !!debouncedSearch || !!builtForm

  if (!hasData) {
    return (
      <StateBox>
        <VStack gap={6}>
          <img src="/images/placeholder-scraping.png" alt="No opportunities" style={{ height: 240 }} />
          <VStack gap={3}>
            <style>{`
              @keyframes gf-dot-pulse {
                0%, 60%, 100% { transform: scale(0.7); opacity: 0.3; }
                30% { transform: scale(1); opacity: 1; }
              }
            `}</style>
            <HStack gap={2}>
              {[0, 0.2, 0.4].map((delay) => (
                <Box
                  key={delay}
                  w="10px"
                  h="10px"
                  borderRadius="full"
                  bg="green.500"
                  style={{ animation: `gf-dot-pulse 1.2s ease-in-out ${delay}s infinite` }}
                />
              ))}
            </HStack>
            <Text fontSize="md" fontWeight="medium" color="gray.700">
              Watching the market for properties matching your criteria
            </Text>
          </VStack>
          <Text fontSize="lg" color="gray.500">
            The list is refreshed daily. You'll be notified by email as new opportunities appear.
          </Text>
        </VStack>
      </StateBox>
    )
  }

  return (
    <VStack gap={4} align="stretch">
      {totalCount > 0 && (
        <Box py={2}>
          <Text fontWeight="semibold" fontSize="md" color="gray.700">
            {totalCount} {totalCount === 1 ? "property" : "properties"}
          </Text>
        </Box>
      )}

      {opportunities.length === 0 && (debouncedSearch || builtForm) && (
        <StateBox>
          <Text color="gray.600">No opportunities match the current filters.</Text>
        </StateBox>
      )}

      {opportunities.length > 0 && viewMode === "map" && (
        <PropertyMapView
          items={mapItems}
          storageKey={`opportunities.sourced.mapViewport.${portfolioId ?? "default"}`}
          onViewDetails={(item) =>
            navigate(`/home/${item.homeId}?opportunityId=${item.opportunityId}`)
          }
          hasMore={hasNextPage}
          isLoadingMore={isFetchingNextPage}
          onLoadMore={() => fetchNextPage()}
        />
      )}

      {opportunities.length > 0 && viewMode === "list" && (
        <VStack gap={4} align="stretch">
          {opportunities.map((opportunity) => (
            <OpportunityCard
              key={opportunity.id}
              opportunity={opportunity}
              onMarkInterested={async (id) => {
                const opp = opportunities.find((o) => o.id === id)
                try {
                  await markMutation.mutateAsync(id)
                  toaster.create({
                    title: "Marked as interested",
                    description: `${opp?.scrapedProperty?.address} has been added to your pipeline.`,
                    type: "success",
                    duration: 4000,
                  })
                } catch {
                  // mutation handles its own error state
                }
              }}
              isMarkingInterested={markMutation.isPending}
            />
          ))}
          {hasNextPage && (
            <Box ref={loadMoreRef} py={4} textAlign="center">
              <HStack justify="center" gap={2}>
                <Spinner size="sm" color="gray.400" />
                <Text color="gray.500">Loading more...</Text>
              </HStack>
            </Box>
          )}
        </VStack>
      )}
    </VStack>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const SOURCED_SORT_OPTIONS = [
  { value: "recent", label: "Most recent" },
  { value: "oldest", label: "Oldest first" },
  { value: "price_asc", label: "Price: low to high" },
  { value: "price_desc", label: "Price: high to low" },
  { value: "bedrooms", label: "Bedrooms" },
  { value: "highest_roi", label: "Highest ROI" },
] as const

const MY_SORT_OPTIONS = [
  { value: "recent", label: "Most recent" },
  { value: "alphabetical", label: "Alphabetical" },
  { value: "oldest", label: "Oldest first" },
  { value: "highest_roi", label: "Highest ROI" },
] as const

const Opportunities: React.FC = () => {
  const navigate = useNavigate()
  const { activePortfolioId, isQuotaReached } = useActivePortfolio()

  const [activeTab, setActiveTab] = useLocalStorage("opportunities.activeTab", "sourced")
  const [builtForm, setBuiltForm] = useLocalStorage("opportunities.builtForm", "")
  const [sourcedOrdering, setSourcedOrdering] = useLocalStorage<OpportunityFilters["ordering"]>(
    "opportunities.ordering",
    "recent"
  )
  const [myOrdering, setMyOrdering] = useLocalStorage<
    "recent" | "alphabetical" | "oldest" | "highest_roi"
  >("opportunities.myOrdering", "recent")
  const [viewMode, setViewMode] = useLocalStorage<ViewMode>("opportunities.viewMode", "list")

  const [searchQuery, setSearchQuery] = useState("")
  const debouncedSearch = useDebounce(searchQuery, 800)
  const currentViewMode: ViewMode = viewMode === "map" ? "map" : "list"

  // Reset filters only when the active portfolio actually changes (not on initial mount)
  const prevPortfolioIdRef = useRef<number | undefined>(undefined)
  useEffect(() => {
    if (activePortfolioId == null) return
    if (prevPortfolioIdRef.current == null) {
      prevPortfolioIdRef.current = activePortfolioId
      return
    }
    if (prevPortfolioIdRef.current === activePortfolioId) return
    prevPortfolioIdRef.current = activePortfolioId
    setSearchQuery("")
    setBuiltForm("")
    setSourcedOrdering("recent")
    setMyOrdering("recent")
    setActiveTab("sourced")
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePortfolioId])

  const isSourcedTab = activeTab === "sourced"
  const sortOptions = isSourcedTab ? SOURCED_SORT_OPTIONS : MY_SORT_OPTIONS
  const sortValue = isSourcedTab ? sourcedOrdering : myOrdering

  const handleSortChange = (value: string) => {
    if (isSourcedTab) {
      setSourcedOrdering(value as OpportunityFilters["ordering"])
    } else {
      setMyOrdering(value as "recent" | "alphabetical" | "oldest" | "highest_roi")
    }
  }

  return (
    <Box p={8}>
      <Heading size="xl" mb={2}>
        Opportunities
      </Heading>
      <Text color="gray.600" mb={6}>
        Properties on the market matching your investment criteria, analysed by Greenflip.
      </Text>

      <HStack justify="space-between" align="center" flexWrap="wrap" gap={3} mb={4}>
        <HStack gap={3} flex={1}>
          <InputGroup
            maxW="320px"
            startElement={
              <InputElement pointerEvents="none">
                <Icon color="gray.400" boxSize={4}>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                </Icon>
              </InputElement>
            }
          >
            <Input
              placeholder="Search address or postcode..."
              value={searchQuery}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
              borderRadius="8px"
            />
          </InputGroup>
          <SelectFilter value={builtForm} onChange={setBuiltForm} placeholder>
            <option value="">All built forms</option>
            <option value="Detached">Detached</option>
            <option value="Semi-Detached">Semi-Detached</option>
            <option value="Mid-Terrace">Mid-Terrace</option>
            <option value="End-Terrace">End-Terrace</option>
          </SelectFilter>
        </HStack>
        <HStack gap={3} align="center">
          {currentViewMode === "list" && (
            <>
              <SelectFilter value={sortValue} onChange={handleSortChange}>
                {sortOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </SelectFilter>
              <Box w="1px" h="24px" bg="gray.200" />
            </>
          )}
          <ViewModeToggle value={currentViewMode} onChange={setViewMode} />
        </HStack>
      </HStack>

      <Tabs.Root
        value={activeTab}
        size="lg"
        onValueChange={(detail) => setActiveTab(detail.value)}
      >
        <Tabs.List mb={6}>
          <Tabs.Trigger value="sourced">Sourced by Greenflip</Tabs.Trigger>
          <Tabs.Trigger value="mine">My Opportunities</Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="sourced">
          <SourcedTab
            portfolioId={activePortfolioId}
            debouncedSearch={debouncedSearch}
            builtForm={builtForm}
            ordering={sourcedOrdering}
            viewMode={currentViewMode}
          />
        </Tabs.Content>

        <Tabs.Content value="mine">
          <PortfolioContent
            showStats={false}
            hideControls
            viewMode={currentViewMode}
            mapStorageKey={`opportunities.mine.mapViewport.${activePortfolioId ?? "default"}`}
            mapDetailFrom="/opportunities"
            externalDebouncedSearch={debouncedSearch}
            externalBuiltForm={builtForm}
            externalSort={myOrdering}
            headerActions={
              <>
                {isBatchImportEnabled && (
                  <Button
                    variant="outline"
                    size="md"
                    onClick={() =>
                      navigate("/bulk-import", {
                        state: { portfolioId: activePortfolioId }
                      })
                    }
                  >
                    Import batch
                  </Button>
                )}
                <Button
                  bg={isQuotaReached ? "gray.400" : "green.800"}
                  color="white"
                  size="md"
                  _hover={{ bg: isQuotaReached ? "gray.400" : "green.700" }}
                  disabled={isQuotaReached}
                  onClick={() =>
                    !isQuotaReached &&
                    navigate("/add-property", {
                      state: { portfolioId: activePortfolioId, from: "/opportunities" }
                    })
                  }
                >
                  {isQuotaReached ? "Quota Reached" : "Add a home"}
                </Button>
              </>
            }
            renderCard={(home) => (
              <HomeOpportunityCard
                home={home}
                onClick={(id) => navigate(`/home/${id}`, { state: { from: "/opportunities" } })}
              />
            )}
          />
        </Tabs.Content>
      </Tabs.Root>
    </Box>
  )
}

export default Opportunities
